/* eslint-disable */
//
// SPIKE-ONLY local replacement for `@cpg-its/ads.auth.auth` (clone of the published snapshot
// 0.0.0-c623894c…, which already supports the `cacheLocation` prop).
//
// ONE behavioral change vs. the package: the AuthHelper calls `getAccessTokenSilently()` on
// MOUNT, instead of only when `isAuthenticated` is already true.
//
// Why: the published helper fetches a token only inside `useEffect(() => { if (isAuthenticated) … })`.
// `isAuthenticated` is driven by the Auth0 provider's `checkSession()`, and auth0-spa-js's
// `checkSession()` SHORT-CIRCUITS (no network / no /authorize) unless a first-party
// `auth0.is.authenticated` cookie is present — a cookie only set after the SDK itself completes a
// login. Our content-page sign-in goes through the hosted signin-qa flow (not the SDK), so that
// cookie is never set, `checkSession` opts out, and cic-nav never even attempts a token.
// `getAccessTokenSilently()` (getTokenSilently) does NOT short-circuit — it goes straight to the
// prompt=none iframe / refresh — so calling it on mount lets cic-nav pick up a hosted-sign-in
// session. A success also seeds the cache + sets the is.authenticated cookie for next time.
//
// Now that QA's Auth0 domain is the custom `authenticate-qa.cpg.org` (same-site with beta.cpg.org),
// that forced iframe is first-party, so it can read the SSO cookie and succeed — i.e. this shim
// should produce a working login on beta. Wired via webpack alias; SPIKE BRANCH ONLY — the real
// fix belongs in the package (add an opt-in `acquireTokenOnMount` prop). Remove shim + aliases
// when that lands.
//
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Auth0Provider, useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import jwtDecode from 'jwt-decode';
import AuthContext from '@cpg-its/ads.auth.auth-context';

const AuthHelper = ({ children }: any) => {
	const [accessToken, setAccessToken] = useState<string | null>(null);
	const [decodedToken, setDecodedToken] = useState<any>(null);
	const [tokenError, setTokenError] = useState<string | null>(null);
	const {
		isAuthenticated,
		loginWithRedirect,
		logout,
		getAccessTokenSilently,
		isLoading,
		error,
	} = useAuth0();

	const getAccessToken = useCallback(async (cacheMode: any = 'on') => {
		try {
			setAccessToken(await getAccessTokenSilently({
				cacheMode: ['on', 'off', 'cache-only'].includes(cacheMode) ? cacheMode : 'on',
			} as any));
		} catch (e) {
			// login_required / no session -> degrade silently (expected when there's no session).
			setTokenError(e instanceof Error ? e.message : 'An unknown error occurred while fetching the access token.');
		}
		return '';
	}, [getAccessTokenSilently]);

	// SPIKE CHANGE: force a silent token fetch on mount (bypasses checkSession's short-circuit)
	// so a hosted-sign-in session is picked up without a prior SDK login.
	useEffect(() => {
		getAccessToken();
	}, [getAccessToken]);

	// Decode whenever we have a token — do NOT gate on isAuthenticated, since a forced silent
	// fetch can yield a token while the provider's checkSession-driven isAuthenticated is false.
	useEffect(() => {
		if (accessToken) {
			setDecodedToken(jwtDecode(accessToken));
		}
	}, [accessToken]);

	const values = useMemo(() => ({
		error,
		isLoading,
		logout,
		loginWithRedirect,
		isAuthenticated,
		getAccessToken,
		accessToken,
		decodedToken,
		tokenError,
		withAuthenticationRequired,
	}), [
		error,
		isLoading,
		logout,
		loginWithRedirect,
		isAuthenticated,
		getAccessToken,
		accessToken,
		decodedToken,
		tokenError,
	]);

	return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

const AuthComponent = ({
	audience = '',
	cacheLocation = 'memory',
	clientId,
	children,
	connection,
	domain,
	redirectUri = window.location.origin,
}: any) => {
	const history = useHistory();
	const onRedirectCallback = (appState: any) => {
		history.push(appState && appState.returnTo ? appState.returnTo : window.location.pathname);
	};

	return (
		<Auth0Provider
			authorizationParams={{
				audience,
				connection,
				redirect_uri: redirectUri,
			}}
			clientId={clientId}
			domain={domain}
			onRedirectCallback={onRedirectCallback}
			useRefreshTokens={true}
			useRefreshTokensFallback={true}
			cacheLocation={cacheLocation}
		>
			<AuthHelper>{children}</AuthHelper>
		</Auth0Provider>
	);
};

export default AuthComponent;
